import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { quotesAPI } from '../../services/api';
import SalespersonLayout from '../../layouts/SalespersonLayout';

const EditQuote = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    web_notes: '',
    discount_percent: 0
  });

  const [products, setProducts] = useState([
    { product_code: '', description: '', price_with_iva: '', quantity: 1 }
  ]);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await quotesAPI.getById(id);
      const quoteData = response.data.quote;
      setQuote(quoteData);

      setFormData({
        client_name: quoteData.client_name || '',
        client_email: quoteData.client_email || '',
        client_phone: quoteData.client_phone || '',
        web_notes: quoteData.web_notes || '',
        discount_percent: quoteData.discount_percent || 0
      });

      if (quoteData.products && quoteData.products.length > 0) {
        setProducts(quoteData.products.map(p => ({
          product_code: p.product_code || '',
          description: p.description || '',
          price_with_iva: p.price_with_iva || '',
          quantity: p.quantity || 1
        })));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar la cotizacion');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = () => {
    setProducts([...products, { product_code: '', description: '', price_with_iva: '', quantity: 1 }]);
  };

  const removeProduct = (index) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  const updateProduct = (index, field, value) => {
    const updated = [...products];
    updated[index][field] = value;
    setProducts(updated);
  };

  const calculateTotals = () => {
    let subtotal = 0;
    products.forEach(p => {
      const price = parseFloat(p.price_with_iva) || 0;
      const qty = parseInt(p.quantity) || 0;
      const priceWithoutIva = price / 1.16;
      subtotal += priceWithoutIva * qty;
    });

    const discountPercent = parseFloat(formData.discount_percent) || 0;
    const discountAmount = subtotal * (discountPercent / 100);
    const subtotalAfterDiscount = subtotal - discountAmount;
    const iva = subtotalAfterDiscount * 0.16;
    const total = subtotalAfterDiscount + iva;

    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      iva: iva.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const totals = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (!formData.client_name.trim()) {
        throw new Error('Ingresa el nombre del cliente');
      }
      if (!formData.web_notes.trim()) {
        throw new Error('El campo WEB es requerido');
      }
      if (products.some(p => !p.description.trim())) {
        throw new Error('Todos los productos deben tener descripcion');
      }
      if (products.some(p => !p.price_with_iva || parseFloat(p.price_with_iva) <= 0)) {
        throw new Error('Todos los productos deben tener precio valido');
      }

      await quotesAPI.update(id, {
        ...formData,
        products: products.map(p => ({
          ...p,
          price_with_iva: parseFloat(p.price_with_iva),
          quantity: parseInt(p.quantity)
        }))
      });

      navigate(`/quotes/${id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al actualizar cotizacion');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SalespersonLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </SalespersonLayout>
    );
  }

  if (!quote) {
    return (
      <SalespersonLayout>
        <div className="text-center py-12">
          <p className="text-red-600">{error || 'Cotizacion no encontrada'}</p>
          <button onClick={() => navigate('/quotes')} className="mt-4 btn btn-primary">
            Volver a Cotizaciones
          </button>
        </div>
      </SalespersonLayout>
    );
  }

  return (
    <SalespersonLayout>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Editar Cotizacion</h2>
          <p className="text-sm text-gray-500">
            {quote.quote_number} - {quote.web_name}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {/* Quote Info (Read-only) */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informacion de Cotizacion</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label text-sm text-gray-500">Numero</label>
                <p className="font-mono font-medium text-primary-600">{quote.quote_number}</p>
              </div>
              <div>
                <label className="label text-sm text-gray-500">Web</label>
                <p className="font-medium">{quote.web_name}</p>
              </div>
              <div>
                <label className="label text-sm text-gray-500">Fecha</label>
                <p className="font-medium">
                  {new Date(quote.quote_date).toLocaleDateString('es-MX', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos del Cliente</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Nombre *</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="input"
                  placeholder="Nombre del cliente"
                  required
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="input"
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <label className="label">Telefono</label>
                <input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="input"
                  placeholder="55 1234 5678"
                />
              </div>
            </div>

            {/* WEB Field */}
            <div className="mt-4">
              <label className="label">WEB *</label>
              <textarea
                value={formData.web_notes}
                onChange={(e) => setFormData({ ...formData, web_notes: e.target.value })}
                className="input w-full min-h-[100px] resize-y"
                placeholder="Ingrese la informacion de la web..."
                required
                style={{ minHeight: '100px' }}
              />
            </div>
          </div>

          {/* Products */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Productos</h2>
              <button
                type="button"
                onClick={addProduct}
                className="btn btn-secondary text-sm"
              >
                + Agregar
              </button>
            </div>

            <div className="space-y-4">
              {products.map((product, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-gray-500">Producto {index + 1}</span>
                    {products.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-2">
                      <label className="label text-xs">Codigo</label>
                      <input
                        type="text"
                        value={product.product_code}
                        onChange={(e) => updateProduct(index, 'product_code', e.target.value)}
                        className="input text-sm"
                        placeholder="SKU"
                      />
                    </div>
                    <div className="md:col-span-5">
                      <label className="label text-xs">Descripcion *</label>
                      <input
                        type="text"
                        value={product.description}
                        onChange={(e) => updateProduct(index, 'description', e.target.value)}
                        className="input text-sm"
                        placeholder="Descripcion del producto"
                        required
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="label text-xs">Precio con IVA *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={product.price_with_iva}
                        onChange={(e) => updateProduct(index, 'price_with_iva', e.target.value)}
                        className="input text-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label text-xs">Cantidad *</label>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity}
                        onChange={(e) => updateProduct(index, 'quantity', e.target.value)}
                        className="input text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Discount & Totals */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Discount */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Descuento</h2>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    className="input w-24"
                  />
                  <span className="text-gray-600">%</span>
                  {parseFloat(formData.discount_percent) > 0 && (
                    <span className="text-green-600 font-medium">
                      -${totals.discountAmount}
                    </span>
                  )}
                </div>
              </div>

              {/* Totals */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Totales</h2>
                <div className="space-y-2 text-right">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${totals.subtotal}</span>
                  </div>
                  {parseFloat(formData.discount_percent) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Descuento ({formData.discount_percent}%):</span>
                      <span>-${totals.discountAmount}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">IVA (16%):</span>
                    <span className="font-medium">${totals.iva}</span>
                  </div>
                  <div className="flex justify-between text-xl pt-2 border-t">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold text-primary-600">${totals.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/quotes/${id}`)}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </SalespersonLayout>
  );
};

export default EditQuote;
